import React, {
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from "react";
import Select from "react-select";
import cs from "classnames";
import debounce from "lodash/debounce";

import { useAuthContext } from "../../../../lib/context/Auth/AuthContext";
import { useDirectorySearch } from "../../../../lib/hooks/useDirectorySearch";

import { Button } from "../../../shared/Button/Button";
import { OrganizerFilter } from "../../Context/Room/Overview/RoomInsightsContextProvider";
import { SearchUserCustomOption } from "../../../shared/Forms/SearchUserInput/SearchUserCustomOption";
import { DirectoryMenuList } from "../../../shared/DirectoryMenuList/DirectoryMenuList";
import { OptionType } from "../../../../lib/types/main.types";
import { CustomNoOptionsMessage } from "../../../shared/NoOptionsMessage/NoOptionsMessage";

interface Props
  extends PropsWithChildren<{
    organizers?: OrganizerFilter[];
    setOrganizers: React.Dispatch<
      React.SetStateAction<OrganizerFilter[] | undefined>
    >;
    handleClose: (() => void) | undefined;
    personalCalendarID: string;
  }> {}

export const HistoryPeopleSelect = ({
  organizers,
  setOrganizers,
  handleClose,
  personalCalendarID,
}: Props) => {
  const [selectedValues, setSelectedValues] = useState<OptionType[] | null>(
    organizers ? organizers : null
  );
  const { user } = useAuthContext();

  const {
    searching,
    searchError,
    searchResults,
    setSearchResults,
    handleDirectorySearch,
    handleIntegrationSearch,
  } = useDirectorySearch();

  const debouncedSearch = useCallback(
    debounce((query: string) => {
      if (query && query.length > 1) {
        handleDirectorySearch(query);
      }
    }, 500),
    [handleDirectorySearch]
  );

  const handleInputChange = (inputValue: string) => {
    if (inputValue && inputValue.length > 1) {
      debouncedSearch(inputValue);
    }
  };

  const handleManualDirectorySearch = (query: string) => {
    if (query) {
      handleIntegrationSearch(query);
    }
  };

  const options = useMemo(() => {
    if (!searchResults?.people?.length) return [];

    const userOption = user
      ? { value: user.email, label: `${user.displayName} (You)` }
      : null;

    const dataOptions =
      searchResults?.people.map((item) => ({
        value: item.email,
        label: item.displayName,
      })) || [];

    return userOption ? [userOption, ...dataOptions] : dataOptions;
  }, [searchResults, user]);

  return (
    <>
      {searchError ? (
        <div>Error fetching people, please try again.</div>
      ) : (
        <>
          <Select
            isMulti
            onChange={(e) => {
              if (e === null) {
                return;
              }
              const values = e.map((item) => ({
                label: item.label,
                value: item.value,
              }));
              setSelectedValues(values);
            }}
            value={selectedValues}
            onInputChange={handleInputChange}
            options={options}
            noOptionsMessage={() => "No result found"}
            onMenuClose={() => {
              if (searchResults?.people?.length) {
                setSearchResults(undefined);
              }
            }}
            isClearable
            components={{
              MenuList: (props) => (
                <DirectoryMenuList
                  {...props}
                  searching={searching}
                  handleDirectorySearch={handleManualDirectorySearch}
                  showNarrowOption={
                    searchResults?.people && searchResults?.people.length > 8
                  }
                />
              ),
              Option: SearchUserCustomOption as any,
              NoOptionsMessage: (props) => (
                <CustomNoOptionsMessage {...props} hideMessage={!!searching} />
              ),
            }}
            className={cs(
              "SearchWorkplaceForm__createableOptions PeopleSelect NoOption"
            )}
          />

          <div className="d-flex justify-content-between">
            <Button
              title="Done"
              size="small"
              color="outline-primary"
              className="Button__extra-small mt-3"
              onClick={() => {
                if (handleClose) {
                  handleClose();
                }

                if (!selectedValues && organizers?.length) {
                  setOrganizers(undefined);
                  return;
                }

                if (!selectedValues) {
                  return;
                }
                setOrganizers(selectedValues);
              }}
            />

            {!!organizers?.length && (
              <Button
                title="Clear"
                size="small"
                color="outline-secondary"
                className="Button__extra-small mt-3"
                onClick={() => {
                  setSelectedValues(null);
                  setOrganizers(undefined);
                }}
              />
            )}
          </div>
        </>
      )}
    </>
  );
};
